import React, { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import sanityClient from "../sanityClient"
import { formatDistanceToNow, parseISO } from "date-fns"
import Breadcrumbs from "../components/support/Breadcrumbs"
import { FaChevronRight } from "react-icons/fa" // Import icon
import ArticleBody from "../components/support/ArticleBody.tsx" // Import the Body component
import { PageLoader } from "../components/Authentication/page-loader"
import useLogging from "../hooks/useLogging"
function calculateLastUpdated(dateString) {
	const date = parseISO(dateString)
	const distance = formatDistanceToNow(date)

	if (distance.includes("year")) return "Updated over a year ago"
	if (distance.includes("month")) return "Updated over a month ago"
	if (distance.includes("week")) return "Updated over a week ago"
	if (distance.includes("day")) return `Updated ${distance.replace("about ", "")} ago`

	return "updated recently"
}

function fetchRelatedPosts(currentPostId) {
	return sanityClient.fetch(
		`*[_type == "post" && _id != $currentPostId] | order(publishedAt desc)[0...3]{
      title,
      slug
    }`,
		{ currentPostId }
	)
}

const emotions = [
	{ emoji: "😞", label: "Bad", value: 2 },
	{ emoji: "😐", label: "Neutral", value: 3 },
	{ emoji: "😄", label: "Very Good", value: 5 }
]

const TableOfContents = ({ headings }) => {
	return (
		<div className="hidden lg:block lg:w-64 lg:fixed lg:top-20 lg:right-10">
			<div className="border-l-2 border-gray-300 pl-4">
				<ul className="space-y-3">
					{headings.map((heading, index) => (
						<li key={index}>
							<Link
								to={heading.id}
								smooth={true}
								duration={500}
								className="text-gray-500 hover:text-black cursor-pointer"
								offset={-10}
							>
								{heading.title}
							</Link>
						</li>
					))}
				</ul>
			</div>
		</div>
	)
}

export default function ArticleDetail({ sidebarOpen }) {
	const { id } = useParams()
	const [postData, setPostData] = useState(null)
	const [relatedPosts, setRelatedPosts] = useState([])
	const [selectedEmotion, setSelectedEmotion] = useState(null)
	const logging = useLogging()
	const [headings, setHeadings] = useState([])

	useEffect(() => {
		const article = document.querySelector(".article-content")
		if (article) {
			const articleHeadings = Array.from(article.querySelectorAll("h2, h3")).map(heading => ({
				id: heading.id || heading.textContent.replace(/\s+/g, "-").toLowerCase(),
				title: heading.textContent,
				level: heading.tagName.toLowerCase()
			}))

			// Set IDs for headings if not present
			articleHeadings.forEach(heading => {
				if (!heading.id) {
					heading.element.id = heading.id
				}
			})

			setHeadings(articleHeadings)
		}
	}, [postData]) // Depend on postData to ensure the content is loaded

	const handleClick = value => {
		setSelectedEmotion(value)
	}

	useEffect(() => {
		sanityClient
			.fetch(
				`*[slug.current == "${id}"]{
          _id,
          title,
          mainImage{
            asset->{
              _id,
              url
            }
          },
          body,
          publishedAt,
          _updatedAt
        }`
			)
			.then(data => {
				setPostData(data[0])
				return data[0]._id
			})
			.then(fetchRelatedPosts)
			.then(setRelatedPosts)
			.catch(error => {
				logging.error("Error fetching article detail:", error)
			})
		// `logging` is a fresh object each render (useLogging returns inline object); fetch fns are stable in practice.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id])

	if (!postData) return <PageLoader />

	const lastUpdated = calculateLastUpdated(postData._updatedAt)

	return (
		<div className={`${sidebarOpen ? "pl-64 pl-8" : "pl-8"}`}>
			<div className="max-w-4xl mx-auto p-6 space-y-10">
				<Breadcrumbs
					items={[
						{ label: "Support", link: "/support" },
						{ label: "Articles", link: "/support/articles" },
						{ label: `${postData.title}` } // No link for the current page
					]}
				/>
				<header className="mb-10 border-b pb-8">
					<h1 className="text-5xl font-extrabold mb-4 text-gray-900 text-left">
						{postData.title}
					</h1>
					<div className="text-gray-500 text-sm mt-4 text-left">
						<span>{lastUpdated}</span>
					</div>
				</header>
				{postData.mainImage && postData.mainImage.asset && (
					<img
						className="w-full h-auto rounded-lg shadow mb-10"
						src={postData.mainImage.asset.url}
						alt={postData.title}
					/>
				)}
				<div className="prose max-w-none mx-auto article-content">
					<ArticleBody blocks={postData.body} />
				</div>
				<hr className="my-12 border-gray-200" />
				<div className="related-posts">
					<h2 className="text-2xl font-bold mb-4 text-gray-800">Related Articles</h2>
					<div className="bg-white shadow rounded-lg p-6">
						<ul className="space-y-4">
							{relatedPosts.map(post => (
								<Link
									to={`/support/articles/${post.slug.current}`}
									className="text-lg font-medium text-black block rounded-lg"
								>
									<li
										key={post.slug.current}
										className="flex justify-between items-center p-4 rounded-lg hover:bg-blue-100 hover:text-customBlue transition-colors duration-200"
									>
										{post.title}
										<FaChevronRight className="text-sm text-customBlue" />{" "}
										{/* Smaller arrow */}
									</li>
								</Link>
							))}
						</ul>
					</div>
				</div>

				<div className="flex flex-col items-center mt-12 bg-gray-100 p-6 rounded-lg">
					<p className="text-lg font-medium text-gray-700 mb-4">
						Did this answer your question?
					</p>
					<div className="flex space-x-6 text-3xl">
						{emotions.map(emotion => (
							<span
								key={emotion.value}
								role="img"
								aria-label={emotion.label}
								className={`cursor-pointer transition-transform ${
									selectedEmotion === emotion.value
										? "scale-125"
										: "hover:scale-110"
								} ${selectedEmotion && selectedEmotion !== emotion.value ? "text-gray-400" : ""}`}
								title={emotion.label}
								onClick={() => handleClick(emotion.value)}
							>
								{emotion.emoji}
							</span>
						))}
					</div>
				</div>

				<div className="flex justify-center mt-12">
					<Link
						to="/support/articles"
						className="text-customBlue hover:text-customBlue-dark flex items-center text-lg font-semibold"
					>
						<span>← Back to All Blog Posts</span>
					</Link>
				</div>

				<TableOfContents headings={headings} />
			</div>
		</div>
	)
}
